// ATM-style currency masking for Rand (ZAR) inputs.
// Digits accumulate from the right (cents-first), e.g. typing "1", "5", "0"
// produces R0.01 → R0.15 → R1.50, matching ATM/POS entry behaviour.
// The displayed value (e.g. "R1.50") is what gets submitted; the server strips
// the "R" prefix before converting to cents.

const RANDS_MAX_CENTS = 99999999; // R999,999.99

const centsToDisplay = (cents) => {
  const rands = Math.floor(cents / 100);
  const c = cents % 100;
  return "R" + rands + "." + String(c).padStart(2, "0");
};

const getCents = (input) => parseInt(input.dataset.cents || "0", 10);

const setCents = (input, cents) => {
  const clamped = Math.min(Math.max(0, cents), RANDS_MAX_CENTS);
  input.dataset.cents = String(clamped);
  input.value = centsToDisplay(clamped);
};

const randsMaskKeydown = (e) => {
  const input = e.currentTarget;
  const key = e.key;

  // readonly only blocks native typing; the mask sets the value itself, so honour it here.
  if (input.readOnly) return;

  if (key >= "0" && key <= "9") {
    e.preventDefault();
    const newCents = Math.min(
      getCents(input) * 10 + parseInt(key, 10),
      RANDS_MAX_CENTS,
    );
    setCents(input, newCents);
    // Bubble an input event so external error-clearing listeners fire.
    input.dispatchEvent(new Event("input", { bubbles: true }));
    return;
  }

  if (key === "Backspace" || key === "Delete") {
    e.preventDefault();
    setCents(input, Math.floor(getCents(input) / 10));
    input.dispatchEvent(new Event("input", { bubbles: true }));
    return;
  }

  // Allow navigation keys; block everything else.
  const allowed = [
    "Tab",
    "ArrowLeft",
    "ArrowRight",
    "ArrowUp",
    "ArrowDown",
    "Home",
    "End",
  ];
  if (!allowed.includes(key)) {
    e.preventDefault();
  }
};

const randsMaskFocus = (input) => {
  // Show R0.00 on focus so the user sees where to start typing.
  if (!input.value || input.value === "") {
    setCents(input, 0);
  }
  // Move cursor to end so it sits after the display value.
  setTimeout(() => {
    try {
      input.setSelectionRange(input.value.length, input.value.length);
    } catch (_) {}
  }, 0);
};

const randsMaskBlur = (input) => {
  // Clear back to empty so the placeholder shows when the field is unfocused at R0.00.
  if (getCents(input) === 0) {
    input.value = "";
    input.dataset.cents = "0";
  }
};

/**
 * Bind the ATM-style Rands currency mask to a single input element.
 * Safe to call multiple times — subsequent calls are no-ops.
 *
 * Pre-fills from any server-rendered value (e.g. edit mode where the
 * server outputs "R1.50" or a plain decimal like "1.5").
 *
 * @param {HTMLInputElement} input
 */
export function bindRandsMask(input) {
  if (input.dataset.randsMaskBound) return;
  input.dataset.randsMaskBound = "1";

  input.addEventListener("keydown", randsMaskKeydown);
  input.addEventListener("focus", () => randsMaskFocus(input));
  input.addEventListener("blur", () => randsMaskBlur(input));

  // Parse any server-rendered value (edit mode): "R1.50" or "1.5" → cents.
  const raw = input.value.replace(/[^0-9.]/g, "");
  if (raw) {
    const cents = Math.round(parseFloat(raw) * 100);
    setCents(input, Number.isNaN(cents) ? 0 : cents);
  } else {
    input.dataset.cents = "0";
    input.value = "";
  }
}

/**
 * Returns the current cents value stored on a masked input.
 * Useful for reading the value before form submission or validation.
 *
 * @param {HTMLInputElement} input
 * @returns {number}
 */
export function getRandsCents(input) {
  return getCents(input);
}
